import React, { useMemo } from "react";
import { useList, useTranslate } from "@refinedev/core";
import { SpeedDistributionPoint } from "types/";
import { AnalyticsBarChart, type BarDatasetConfig } from "./AnalyticsBarChart";
import { analyticsTheme } from "./analyticsTheme";
import { getCountAxis, getCategoryAxis } from "./utils/";
import { ANALYTICS_STALE_TIME } from "./constants/constants";

const T_PREFIX =
  "media.projects.analytics.dashboard.summaryCards.speedDistribution";

interface SpeedDistributionChartProps {
  projectId: string | undefined;
  /** Shown next to the title. */
  badge?: React.ReactNode;
}

export const SpeedDistributionChart: React.FC<SpeedDistributionChartProps> = ({
  projectId,
  badge,
}) => {
  const t = useTranslate();
  const chartOptions = useMemo(
    () => ({ scales: { y: getCountAxis(), x: getCategoryAxis() } }),
    []
  );

  const {
    data: speedData,
    isLoading,
    isFetching,
  } = useList<SpeedDistributionPoint>({
    resource: `analytics/projects/${projectId}/analytics/video/speed-distribution`,
    queryOptions: {
      enabled: !!projectId,
      staleTime: ANALYTICS_STALE_TIME,
    },
    errorNotification: false,
  });

  const points = speedData?.data ?? [];

  const labels = useMemo(
    () => points.map((p) => `${p.final_playback_rate}x`),
    [points]
  );

  const datasets = useMemo<BarDatasetConfig[]>(
    () => [
      {
        label: t(`${T_PREFIX}.sessions`),
        data: points.map((p) => p.session_count),
        color: analyticsTheme.datasetEmerald,
      },
    ],
    [points, t]
  );

  return (
    <AnalyticsBarChart
      title={t(`${T_PREFIX}.title`)}
      badge={badge}
      labels={labels}
      datasets={datasets}
      loading={isLoading || isFetching}
      direction="vertical"
      height={200}
      chartOptions={chartOptions}
    />
  );
};
